import { ReactComponent as CameraSvg } from "@/components/ui/icons/pano-measure-camera.svg";
import { ReactComponent as WallCeiling } from "@/components/ui/icons/pano-measure-wall-ceiling.svg";
import { ReactComponent as WallCorner1Svg } from "@/components/ui/icons/pano-measure-wall-corner-1.svg";
import { ReactComponent as WallCorner2Svg } from "@/components/ui/icons/pano-measure-wall-corner-2.svg";

import { useCurrentScene } from "@/modes/mode-data-context";
import { PanoObject, useCached3DObjectIfReady } from "@/object-cache";
import { isPanoObject } from "@/object-cache-type-guard";
import {
  selectCameraHeight,
  selectPanoMeasurementToolState,
} from "@/store/measurement-tool-selector";
import {
  CAMERA_HEIGHT_DEFAULT,
  CAMERA_HEIGHT_MIN,
  PanoMeasurementToolState,
  setCameraHeight,
  setPanoMeasurementToolState,
} from "@/store/measurement-tool-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import {
  ArrowDownIcon,
  blue,
  FaroButton,
  FaroIconButton,
  FaroSvgIcon,
  FaroText,
  grey,
  NO_TRANSLATE_CLASS,
  ToggleButton,
} from "@faro-lotv/flat-ui";
import { Link, Paper, Tooltip } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import {
  formatDistanceDecimal,
  getDistanceUnitSymbolText,
} from "../common/settings-context/distance-unit-utils";
import { useDistanceUnit } from "../common/settings-context/settings-context";
import { DistanceInputField } from "./distance-input-field";

/**
 * @returns The panel for the pano measurement tool
 */
export function PanoMeasurementToolPanel(): JSX.Element | null {
  const { main } = useCurrentScene();
  const pano = useCached3DObjectIfReady(main, "PanoMeasurementToolPanel");

  const currentState = useAppSelector(selectPanoMeasurementToolState);
  const measureActive =
    useAppSelector(selectActiveTool) === ToolName.measurement;

  const [expanded, setExpanded] = useState(true);

  if (
    pano instanceof Error ||
    !isPanoObject(pano) ||
    !measureActive ||
    currentState === PanoMeasurementToolState.None
  ) {
    return null;
  }

  if (expanded) {
    return (
      <PanoMeasurementToolPanelExpanded
        pano={pano}
        currentState={currentState}
        setExpanded={setExpanded}
      />
    );
  }

  return <PanoMeasurementToolPanelCollapsed setExpanded={setExpanded} />;
}

type PanoMeasurePanelProps = {
  pano: PanoObject;
  currentState: PanoMeasurementToolState;
  setExpanded?(expanded: boolean): void;
};

function PanoMeasurementToolPanelExpanded({
  pano,
  currentState,
  setExpanded,
}: PanoMeasurePanelProps): JSX.Element {
  return (
    <Box
      component="div"
      sx={{
        p: 1.5,
        width: "300px",
        borderRadius: "4px",
        backgroundColor: grey[1000],
      }}
    >
      <Stack direction="column" spacing={1}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <PanoMeasureToolbar currentState={currentState} />
          <FaroIconButton
            size="m"
            color={grey[500]}
            onClick={() => setExpanded?.(false)}
          >
            <ArrowDownIcon
              sx={{
                transform: "rotate(-90deg)",
              }}
            />
          </FaroIconButton>
        </Stack>
        {currentState === PanoMeasurementToolState.SetCameraHeight && (
          <SetCameraHeightStep pano={pano} />
        )}
        {isAddWallState(currentState) && (
          <AddWallStep currentState={currentState} />
        )}
        {currentState === PanoMeasurementToolState.Measuring && <MeasureStep />}
      </Stack>
    </Box>
  );
}

function PanoMeasurementToolPanelCollapsed({
  setExpanded,
}: Pick<PanoMeasurePanelProps, "setExpanded">): JSX.Element {
  return (
    <Box
      component="div"
      sx={{
        borderRadius: "20px 4px 4px 20px",
        backgroundColor: grey[1000],
      }}
    >
      <Stack direction="row">
        <FaroIconButton
          size="m"
          color={grey[500]}
          onClick={() => setExpanded?.(true)}
        >
          <ArrowDownIcon
            sx={{
              transform: "rotate(90deg)",
            }}
          />
        </FaroIconButton>
      </Stack>
    </Box>
  );
}

function PanoMeasureToolbar({
  currentState,
}: Pick<PanoMeasurePanelProps, "currentState">): JSX.Element {
  const dispatch = useAppDispatch();
  const setToolState = useCallback(
    (state: PanoMeasurementToolState) => {
      if (currentState !== state) {
        dispatch(setPanoMeasurementToolState(state));
      }
    },
    [dispatch, currentState],
  );

  const setToAddWallState = useCallback(() => {
    if (!isAddWallState(currentState)) {
      setToolState(PanoMeasurementToolState.SelectFloorPoint1);
    }
  }, [currentState, setToolState]);

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        backgroundColor: grey[900],
        padding: "4px",
        border: 0,
      }}
    >
      <Stack
        direction="row"
        spacing="4px"
        sx={{ width: "100%", alignItems: "center" }}
      >
        <PanoMeasureToolBarButton
          title="Camera"
          selected={currentState === PanoMeasurementToolState.SetCameraHeight}
          onClick={() => setToolState(PanoMeasurementToolState.SetCameraHeight)}
        />
        <PanoMeasureToolBarButton
          title="Add wall"
          selected={isAddWallState(currentState)}
          onClick={setToAddWallState}
        />
        <PanoMeasureToolBarButton
          title="Measure"
          selected={currentState === PanoMeasurementToolState.Measuring}
          onClick={() => setToolState(PanoMeasurementToolState.Measuring)}
        />
      </Stack>
    </Paper>
  );
}

type PanoMeasureToolBarButtonProps = {
  title: string;
  selected: boolean;
  onClick(): void;
};

function PanoMeasureToolBarButton({
  title,
  selected,
  onClick,
}: PanoMeasureToolBarButtonProps): JSX.Element {
  return (
    <Tooltip title={title} placement="top">
      <ToggleButton
        value={title}
        fullWidth
        onClick={onClick}
        sx={{
          height: "32px",
          backgroundColor: selected ? grey[1000] : grey[900],
          border: 0,
          padding: "4px",
        }}
      >
        <FaroText variant="heading14" color={selected ? blue[400] : grey[500]}>
          {title}
        </FaroText>
      </ToggleButton>
    </Tooltip>
  );
}

function SetCameraHeightStep({
  pano,
}: Pick<PanoMeasurePanelProps, "pano">): JSX.Element {
  const dispatch = useAppDispatch();
  const cameraHeight = useAppSelector(selectCameraHeight(pano.iElement.id));
  const distanceUnit = useDistanceUnit();

  const [heightError, setHeightError] = useState<string>();
  const setHeight = useCallback(
    (height: number | undefined) => {
      setHeightError(undefined);
      if (height === undefined) return;
      if (height < CAMERA_HEIGHT_MIN) {
        setHeightError(
          `Please enter a value > ${formatDistanceDecimal(CAMERA_HEIGHT_MIN, distanceUnit, true)}.`,
        );
        return;
      }
      dispatch(setCameraHeight({ panoId: pano.iElement.id, height }));
    },
    [dispatch, distanceUnit, pano.iElement.id],
  );

  useEffect(() => {
    if (cameraHeight === undefined) {
      setHeight(CAMERA_HEIGHT_DEFAULT);
    }
  }, [cameraHeight, setHeight]);

  return (
    <Stack direction="column" spacing={1} sx={{ alignContent: "center" }}>
      <FaroText variant="labelL" dark>
        Enter the distance from the ground to the camera lens.{" "}
        <i>Accurate measurements depend on this value.</i>
      </FaroText>
      <Stack direction="row" sx={{ justifyContent: "center" }}>
        <FaroSvgIcon
          source={CameraSvg}
          sx={{
            width: "110px",
            height: "80px",
            fill: "none",
          }}
        />
      </Stack>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <FaroText variant="labelL" dark sx={{ width: "90%" }}>
          Camera height:
        </FaroText>
        <DistanceInputField
          variant="underlined"
          fullWidth
          dark
          autoFocus
          distanceInMeter={cameraHeight ?? CAMERA_HEIGHT_DEFAULT}
          distanceUnit={distanceUnit}
          onDistanceChanged={setHeight}
          customValidationError={heightError}
        />
        <FaroText variant="labelL" dark className={NO_TRANSLATE_CLASS}>
          {getDistanceUnitSymbolText(distanceUnit)}
        </FaroText>
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", justifyContent: "flex-end" }}
      >
        <LearnMoreLink />
        <FaroButton
          variant="primary"
          size="m"
          onClick={() => {
            dispatch(
              setPanoMeasurementToolState(
                PanoMeasurementToolState.SelectFloorPoint1,
              ),
            );
          }}
        >
          Next
        </FaroButton>
      </Stack>
    </Stack>
  );
}

function AddWallStep({
  currentState,
}: Pick<PanoMeasurePanelProps, "currentState">): JSX.Element | null {
  const { icon, msg, step } = useMemo(() => {
    let icon = null;
    let msg = null;
    let step = 0;
    switch (currentState) {
      case PanoMeasurementToolState.SelectFloorPoint1:
        icon = WallCorner1Svg;
        msg = (
          <FaroText variant="labelL" dark>
            Pick <b>a bottom corner</b> of the wall.
          </FaroText>
        );
        step = 1;
        break;
      case PanoMeasurementToolState.SelectFloorPoint2:
        icon = WallCorner2Svg;
        msg = (
          <FaroText variant="labelL" dark>
            Pick the <b>opposite bottom corner</b>.
          </FaroText>
        );
        step = 2;
        break;
      case PanoMeasurementToolState.SelectCeilingPoint:
        icon = WallCeiling;
        msg = (
          <FaroText variant="labelL" dark>
            Pick a <b>point at the top</b> of the wall.
          </FaroText>
        );
        step = 3;
        break;
    }
    return { icon, msg, step };
  }, [currentState]);

  if (!icon || !msg || !step) {
    return null;
  }

  return (
    <Stack direction="column" spacing={1} sx={{ alignContent: "center" }}>
      {msg}
      <Stack direction="row" sx={{ justifyContent: "center" }}>
        <FaroSvgIcon
          source={icon}
          sx={{
            width: "110px",
            height: "80px",
            fill: "none",
          }}
        />
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", justifyContent: "space-between" }}
      >
        <FaroText variant="labelL" color={grey[500]}>
          Step {step}/3
        </FaroText>
        <LearnMoreLink />
      </Stack>
    </Stack>
  );
}

function MeasureStep(): JSX.Element {
  return (
    <Stack direction="column" spacing={1} sx={{ alignContent: "center" }}>
      <FaroText variant="labelL" dark>
        Click on the wall to start measuring.
      </FaroText>
      <FaroText variant="labelL" dark>
        To add another wall, select <b>Add wall</b> from the toolbar.
      </FaroText>
      <FaroText variant="labelL" dark>
        Hold <b>SHIFT</b> to lock measurements to horizontal or vertical lines.
      </FaroText>
    </Stack>
  );
}

function LearnMoreLink(): JSX.Element {
  return (
    <Link
      href="https://help.holobuilder.com/en/articles/12890208-make-reliable-measurements-in-your-360-photos"
      target="_blank"
      rel="noopener noreferrer"
    >
      <FaroText variant="hyperLink">Learn more</FaroText>
    </Link>
  );
}

/**
 * @param state The pano measurement tool state
 * @returns whether the state is one of adding wall states
 */
export function isAddWallState(state: PanoMeasurementToolState): boolean {
  return (
    state === PanoMeasurementToolState.SelectFloorPoint1 ||
    state === PanoMeasurementToolState.SelectFloorPoint2 ||
    state === PanoMeasurementToolState.SelectCeilingPoint
  );
}
